import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ClientService } from '@zaoblako/web/core/services/client/client.service';
import { AutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/auto-complete/auto-complete.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ClientsListQueryInterface } from '@zaoblako/shared/modules/client/dtos/client/clients-list-query.dto';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';

import { finalize } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'crm-client-auto-complete',
  templateUrl: './client-auto-complete.component.html',
  imports: [CommonModule, AutoCompleteComponent, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientAutoCompleteComponent implements OnChanges {
  @ViewChild('clientAutoComplete', { static: true }) public clientAutoComplete: AutoCompleteComponent;

  @Input() public selectedClient: ClientFindInterface;
  @Output() public selectedClientChange = new EventEmitter<ClientFindInterface>();

  @Input() public clients: ClientFindInterface[] = [];
  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string | string[] | Set<string> | { [klass: string]: any };
  @Input() public inputStyle: any;
  @Input() public inputStyleClass: string;
  @Input() public placeholder: string;
  @Input() public disabled = false;
  @Input() public appendTo: string | null;
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  @Input() public showClear = false;
  @Input() public loading = false;

  @Input() public customSearch = true;
  @Input() public isCreateTransitTask = false;
  @Output() public clientSearch = new EventEmitter<string>();

  public selectionLoading = false;
  private hydrationRequestId = 0;

  public constructor(
    private readonly clientService: ClientService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedClient?.currentValue && !this.customSearch && !this.selectedClient.contragents) {
      const requestId = ++this.hydrationRequestId;
      const selectedClientId = this.selectedClient.id;
      this.selectionLoading = true;
      this.cdRef.markForCheck();

      this.clientService
        .findAll({
          search: this.selectedClient?.name,
          select: ['id', 'name', 'contragents', 'externalUsers', 'filials'],
          ...this.clientListFilters()
        })
        .pipe(
          finalize(() => {
            if (requestId === this.hydrationRequestId) {
              this.selectionLoading = false;
              this.cdRef.markForCheck();
            }
          })
        )
        .subscribe((clients) => {
          if (requestId === this.hydrationRequestId && selectedClientId === this.selectedClient?.id) {
            this.clients = clients;
            this.selectedClient = this.clients.find((_client) => _client.id === selectedClientId);
            this.cdRef.markForCheck();
          }
        });
    } else if (changes.selectedClient) {
      this.hydrationRequestId++;
      this.selectionLoading = false;
    }
  }

  public toClient(client: any): ClientFindInterface {
    return client as ClientFindInterface;
  }

  public searchClient(name: string) {
    this.clientService
      .findAll({
        search: name,
        select: ['id', 'name', 'contragents', 'externalUsers', 'filials'],
        ...this.clientListFilters()
      })
      .subscribe((clients) => {
        this.clients = clients;
        this.cdRef.markForCheck();
      });
  }

  private clientListFilters(): Pick<ClientsListQueryInterface, 'isCreateTransitTask'> {
    return this.isCreateTransitTask ? { isCreateTransitTask: true } : {};
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.clientAutoComplete?.autoComplete?.inputValue());
  }
}
